'use client';

import React, { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/ui/AppIcon';

export default function HelpCenterPage() {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const faqs = [
        { q: t('faqHowToFind'), a: t('faqHowToFindAnswer') },
        { q: t('faqVerified'), a: t('faqVerifiedAnswer') },
        { q: t('faqRequestQuote'), a: t('faqRequestQuoteAnswer') },
        { q: t('faqPayment'), a: t('faqPaymentAnswer') },
    ];

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                {/* Hero */}
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-semibold mb-6">
                            <Icon name="QuestionMarkCircleIcon" size={16} variant="outline" />
                            <span>{t('supportAvailable')}</span>
                        </div>
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">
                            {t('helpCenter')}
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                            {t('helpCenterDesc')}
                        </p>
                        {/* Search */}
                        <div className="max-w-2xl mx-auto relative">
                            <Icon name="MagnifyingGlassIcon" size={20} variant="outline" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={t('searchForHelp')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                            />
                        </div>
                    </div>
                </section>

                {/* FAQs */}
                <section className="py-20 px-6">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('faqTitle')}</h2>
                        <div className="space-y-4">
                            {faqs.map((faq, i) => (
                                <div key={i} className="bg-card border border-border rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                                    >
                                        <span className="font-semibold text-foreground">{faq.q}</span>
                                        <Icon name={openIndex === i ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} variant="outline" className="text-muted-foreground" />
                                    </button>
                                    {openIndex === i && (
                                        <div className="px-6 pb-4 text-muted-foreground">{faq.a}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact */}
                <section className="py-20 px-6 bg-card">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">{t('stillNeedHelp')}</h2>
                        <p className="text-muted-foreground mb-8">{t('contactSupportTeam')}</p>
                        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                            <a href="mailto:support@egyptexgateway.com" className="flex items-center gap-4 p-6 bg-background border border-border rounded-lg hover:shadow-md transition-shadow">
                                <Icon name="EnvelopeIcon" size={32} variant="outline" className="text-primary" />
                                <div className="text-left">
                                    <div className="font-semibold text-foreground">{t('emailSupport')}</div>
                                    <div className="text-sm text-muted-foreground">support@egyptexgateway.com</div>
                                </div>
                            </a>
                            <a href="tel:+201044868081" className="flex items-center gap-4 p-6 bg-background border border-border rounded-lg hover:shadow-md transition-shadow">
                                <Icon name="PhoneIcon" size={32} variant="outline" className="text-primary" />
                                <div className="text-left">
                                    <div className="font-semibold text-foreground">{t('phoneSupport')}</div>
                                    <div className="text-sm text-muted-foreground">+20 10 44868081</div>
                                </div>
                            </a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
